import React, { useState, useEffect } from 'react';
import { Linkedin, Facebook } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import './css/Navbar.css';
import axios from 'axios';
import logoImage from '../Assets/Image/logo.webp';
import { useNavigationConfig } from '../context/NavigationConfigContext';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(logoImage);
  const { links } = useNavigationConfig();

  const resolveLogoUrl = (rawLogoUrl) => {
    if (!rawLogoUrl) return logoImage;

    const isAbsolute =
      typeof rawLogoUrl === 'string' &&
      (rawLogoUrl.startsWith('http://') || rawLogoUrl.startsWith('https://'));

    const resolved = isAbsolute
      ? rawLogoUrl
      : `${API_BASE_URL}${rawLogoUrl.startsWith('/') ? '' : '/'}${rawLogoUrl}`;

    // Cache-bust the logo URL to ensure updates are visible immediately.
    const joiner = resolved.includes('?') ? '&' : '?';
    return `${resolved}${joiner}cb=${Date.now()}`;
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isMenuOpen]);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/branding`);
        const rawLogoUrl = response.data?.logoUrl;

        if (!rawLogoUrl) {
          return;
        }

        setLogoUrl(resolveLogoUrl(rawLogoUrl));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    };

    fetchBranding();
  }, []);

  useEffect(() => {
    const handleBrandingUpdated = (event) => {
      const rawLogoUrl = event?.detail?.logoUrl;
      if (!rawLogoUrl) return;
      setLogoUrl(resolveLogoUrl(rawLogoUrl));
    };

    window.addEventListener('brandingUpdated', handleBrandingUpdated);
    return () => window.removeEventListener('brandingUpdated', handleBrandingUpdated);
  }, []);

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);
  const closeMenu = () => setIsMenuOpen(false);

  const handlePillarClick = (pillarId) => {
    navigate('/services');
    setDropdownOpen(false);
    // Scroll to pillar section after navigation
    setTimeout(() => {
      const element = document.getElementById(`pillar-${pillarId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <>
      <nav className={`dynamic-navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="navbar-logo-wrapper">
          <div className="navbar-logo">
            <Link to="/">
            <img
              src={logoUrl}
              alt="Whales Advisory"
              className="logo-img"
              onError={() => setLogoUrl(logoImage)}
            />
            </Link>
          </div>
        </div>

        <div className="navbar-links-wrapper">
          <ul className="navbar-links">
            {links.map((link) => {
              const isActive = location.pathname === link.path;

              if (link.type === 'services') {
                return (
                  <li
                    key={link.id}
                    className={`dropdown-trigger ${isActive ? 'active' : ''}`}
                    onMouseEnter={() => setDropdownOpen(true)}
                    onMouseLeave={() => setDropdownOpen(false)}
                    onClick={() => navigate(link.path)}
                  >
                    {link.label}
                    {dropdownOpen && (
                      <ul
                        className="dropdown-menu"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <li onClick={() => handlePillarClick('corporate')}>
                          Corporate Advisory
                        </li>
                        <li onClick={() => handlePillarClick('digital')}>
                          Digital Platform
                        </li>
                        <li onClick={() => handlePillarClick('international')}>
                          International and Regional Firms
                        </li>
                      </ul>
                    )}
                  </li>
                );
              }

              return (
                <li
                  key={link.id}
                  className={isActive ? 'active' : ''}
                  onClick={() => navigate(link.path)}
                >
                  {link.label}
                </li>
              );
            })}
          </ul>
        </div>

        <button
          type="button"
          className="mobile-menu-toggle"
          aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={isMenuOpen}
          onClick={toggleMenu}
        >
          <span className="hamburger-line" />
          <span className="hamburger-line" />
          <span className="hamburger-line" />
        </button>
      </nav>

      <div className={`mobile-menu-drawer ${isMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-inner">
          <header className="mobile-menu-header">
            <img
              src={logoUrl}
              alt="Whales Advisory"
              className="mobile-menu-logo"
              onError={() => setLogoUrl(logoImage)}
            />
            <button
              type="button"
              className="mobile-menu-close"
              aria-label="Close navigation menu"
              onClick={closeMenu}
            >
              <span />
              <span />
            </button>
          </header>
          <nav className="mobile-menu-nav">
            <ul className="mobile-menu-links">
              {links.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <li
                    key={link.id}
                    className={isActive ? 'active' : ''}
                    onClick={() => {
                      closeMenu();
                      navigate(link.path);
                    }}
                  >
                    {link.label}
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
}

export default Navbar;